package org.gymly.ui.admin

import androidx.compose.material3.minimumInteractiveComponentSize
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.horizontalScroll
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.KeyboardActions
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.outlined.Close
import androidx.compose.material.icons.outlined.Delete
import androidx.compose.material.icons.outlined.Edit
import androidx.compose.material.icons.outlined.Search
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.Checkbox
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.DropdownMenu
import androidx.compose.material3.DropdownMenuItem
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.OutlinedTextFieldDefaults
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.ReadOnlyComposable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.runtime.key
import androidx.compose.ui.Alignment
import androidx.compose.ui.layout.ContentScale
import coil3.compose.AsyncImage
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.focus.FocusRequester
import androidx.compose.ui.focus.focusRequester
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.input.key.Key
import androidx.compose.ui.input.key.KeyEventType
import androidx.compose.ui.input.key.isShiftPressed
import androidx.compose.ui.input.key.key
import androidx.compose.ui.input.key.onPreviewKeyEvent
import androidx.compose.ui.input.key.type
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.ImeAction
import androidx.compose.ui.text.input.PasswordVisualTransformation
import androidx.compose.ui.text.input.VisualTransformation
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.compose.ui.window.Dialog
import kotlinx.coroutines.launch
import org.gymly.data.AdminRepo
import org.gymly.data.persistClientPhotoAndSync
import org.gymly.i18n.clientsStrings
import org.gymly.i18n.label
import org.gymly.i18n.strings
import org.gymly.model.AdminClient
import org.gymly.model.AdminClientStatus
import org.gymly.model.AdminPortalActivation
import org.gymly.model.AdminClientUpsert
import org.gymly.model.AdminPersonSex
import org.gymly.model.PickedImagePayload
import org.gymly.theme.AdminColors
import org.gymly.theme.GymlySpace
import org.gymly.util.imagePreviewModel
import org.gymly.util.isValidEmailOrBlank
import org.gymly.util.openImagePicker
import org.gymly.util.userMessage
import kotlin.time.Clock
import kotlin.time.Duration.Companion.days
import kotlin.time.Instant
import org.gymly.ui.components.VerticalScrollColumn
import org.gymly.ui.components.PasswordRules
import org.gymly.ui.components.PasswordRulesHint
import org.gymly.ui.components.PasswordVisibilityToggle
import org.gymly.ui.components.rememberPasswordVisible

private enum class ClientsDateFilter(val days: Int?) {
    Days7(7),
    Days30(30),
    All(null),
}

@Composable
@ReadOnlyComposable
private fun ClientsDateFilter.label(): String {
    val tc = clientsStrings()
    return when (this) {
        ClientsDateFilter.Days7 -> tc.filterLast7Days
        ClientsDateFilter.Days30 -> tc.filterLast30Days
        ClientsDateFilter.All -> tc.filterAllDates
    }
}

private data class ClientFormSubmit(
    val upsert: AdminClientUpsert,
    val photo: PickedImagePayload? = null,
    val removePhoto: Boolean = false,
)

private sealed class ClientsDialog {
    data object Create : ClientsDialog()
    data class Edit(val client: AdminClient) : ClientsDialog()
    data class Detail(val client: AdminClient) : ClientsDialog()
    data class Delete(val client: AdminClient) : ClientsDialog()
}

@Composable
fun AdminClientsScreen() {
    val t = strings()
    val tc = clientsStrings()
    val scope = rememberCoroutineScope()
    var clients by remember { mutableStateOf<List<AdminClient>>(emptyList()) }
    var companyId by remember { mutableStateOf<String?>(null) }
    var loading by remember { mutableStateOf(true) }
    var error by remember { mutableStateOf<String?>(null) }
    var successMsg by remember { mutableStateOf<String?>(null) }
    val pendingApprovals = LocalPendingApprovalNotifier.current
    var query by remember { mutableStateOf("") }
    var dateFilter by remember { mutableStateOf(ClientsDateFilter.All) }
    var dialog by remember { mutableStateOf<ClientsDialog?>(null) }
    var saving by remember { mutableStateOf(false) }

    fun reload() {
        scope.launch {
            loading = true
            error = null
            try {
                clients = AdminRepo.api.listClients()
                companyId = runCatching { AdminRepo.api.getCompany().id }.getOrElse {
                    AdminRepo.api.listBranches().firstOrNull()?.id
                }
            } catch (e: Exception) {
                error = e.userMessage(tc.clientsLoadFailed)
            } finally {
                loading = false
            }
        }
    }

    LaunchedEffect(LocalAdminDataRevision.current) { reload() }

    val filtered = remember(clients, query, dateFilter) {
        val q = query.trim().lowercase()
        val cutoff = dateFilter.days?.let { Clock.System.now() - it.days }
        clients.filter { c ->
            val matchesQuery = q.isEmpty() ||
                c.fullName.lowercase().contains(q) ||
                (c.email?.lowercase()?.contains(q) == true) ||
                (c.membershipPlanName?.lowercase()?.contains(q) == true)
            val matchesDate = if (cutoff == null) {
                true
            } else {
                val created = c.createdAt?.let { runCatching { Instant.parse(it) }.getOrNull() }
                created == null || created >= cutoff
            }
            matchesQuery && matchesDate
        }
    }

    val listPaging = rememberAdminListPaging(
        initialSortKey = "name",
        resetKey = listOf(query, dateFilter),
    )
    val pageItems = remember(filtered, listPaging.sort, listPaging.page) {
        filtered.adminSortedPaged(listPaging.sort, listPaging.page) { key ->
            when (key) {
                "status" -> { c -> c.status.name }
                "portal" -> { c -> if (c.portalEnabled) 1 else 0 }
                else -> { c -> c.fullName.lowercase() }
            }
        }
    }

    val kpiSource = filtered
    val total = kpiSource.size
    val active = kpiSource.count { it.status == AdminClientStatus.ACTIVE }
    val inactive = kpiSource.count { it.status == AdminClientStatus.INACTIVE }
    val licensed = rememberLicensedAppChannels()
    val withApp = kpiSource.count { appChannelLabels(it, licensed.orEmpty()).isNotEmpty() }

    BoxWithConstraints(
        Modifier
            .fillMaxSize()
            .background(AdminColors.Surface)
            .padding(GymlySpace.lg),
    ) {
        val compact = maxWidth < 700.dp
        // Scroll de página (como Ventas): la tabla crece hasta 20 filas sin viewport interno corto.
        VerticalScrollColumn(Modifier.fillMaxSize()) {
            ClientsHeader(
                dateFilter = dateFilter,
                onFilterChange = { dateFilter = it },
            )
            Spacer(Modifier.height(16.dp))
            ClientsKpiRow(
                total = total,
                active = active,
                inactive = inactive,
                withApp = withApp,
                compact = compact,
            )
            Spacer(Modifier.height(16.dp))
            error?.let {
                Text(it, color = AdminColors.Error, fontSize = 13.sp, modifier = Modifier.padding(bottom = 8.dp))
            }
            successMsg?.let {
                Text(it, color = AdminColors.Primary, fontSize = 13.sp, modifier = Modifier.padding(bottom = 8.dp))
            }
            ClientsDirectoryCard(
                clients = pageItems,
                totalCount = filtered.size,
                listPaging = listPaging,
                loading = loading,
                query = query,
                onQueryChange = { query = it },
                compact = compact,
                onCreate = { dialog = ClientsDialog.Create },
                onView = { dialog = ClientsDialog.Detail(it) },
                onEdit = { dialog = ClientsDialog.Edit(it) },
                onDelete = { dialog = ClientsDialog.Delete(it) },
                licensed = licensed,
            )
        }
    }

    when (val d = dialog) {
        is ClientsDialog.Detail -> ClientDetailDialog(
            client = d.client,
            licensed = licensed,
            onDismiss = { dialog = null },
        )
        is ClientsDialog.Create -> ClientFormDialog(
            title = tc.addClientTitle,
            initial = null,
            companyId = companyId,
            saving = saving,
            onDismiss = { if (!saving) dialog = null },
            onSave = { submit ->
                scope.launch {
                    saving = true
                    error = null
                    try {
                        val created = AdminRepo.api.createClient(submit.upsert)
                        if (submit.photo != null || submit.removePhoto) {
                            AdminRepo.api.persistClientPhotoAndSync(
                                created.id,
                                photo = submit.photo,
                                removePhoto = submit.removePhoto,
                            )
                        } else {
                            runCatching { AdminRepo.api.syncClientAccess(created.id) }
                        }
                        dialog = null
                        reload()
                    } catch (e: Exception) {
                        error = e.userMessage(tc.clientSaveFailed)
                    } finally {
                        saving = false
                    }
                }
            },
        )
        is ClientsDialog.Edit -> ClientFormDialog(
            title = tc.editClientTitle,
            initial = d.client,
            companyId = companyId,
            saving = saving,
            onDismiss = { if (!saving) dialog = null },
            onSave = { submit ->
                scope.launch {
                    saving = true
                    error = null
                    try {
                        AdminRepo.api.updateClient(d.client.id, submit.upsert)
                        if (submit.photo != null || submit.removePhoto) {
                            AdminRepo.api.persistClientPhotoAndSync(
                                d.client.id,
                                photo = submit.photo,
                                removePhoto = submit.removePhoto,
                            )
                        } else {
                            runCatching { AdminRepo.api.syncClientAccess(d.client.id) }
                        }
                        dialog = null
                        reload()
                    } catch (e: Exception) {
                        error = e.userMessage(tc.clientSaveFailed)
                    } finally {
                        saving = false
                    }
                }
            },
        )
        is ClientsDialog.Delete -> AlertDialog(
            onDismissRequest = { if (!saving) dialog = null },
            title = { Text(tc.deleteClientTitle) },
            text = {
                Text(tc.deleteClientConfirm(d.client.fullName))
            },
            confirmButton = {
                Button(
                    onClick = {
                        scope.launch {
                            saving = true
                            error = null
                            try {
                                AdminRepo.api.deleteClient(d.client.id)
                                dialog = null
                                successMsg = null
                                reload()
                            } catch (e: Exception) {
                                if (pendingApprovals.offer(e)) {
                                    dialog = null
                                    successMsg = null
                                    error = null
                                } else {
                                    dialog = null
                                    error = e.userMessage(tc.clientDeleteFailed)
                                }
                            } finally {
                                saving = false
                            }
                        }
                    },
                    enabled = !saving,
                    colors = ButtonDefaults.buttonColors(
                        containerColor = AdminColors.Primary,
                        contentColor = AdminColors.OnBrand,
                    ),
                ) {
                    Text(if (saving) "…" else t.common.delete)
                }
            },
            dismissButton = {
                TextButton(onClick = { dialog = null }, enabled = !saving) {
                    Text(t.common.cancel, color = AdminColors.TextMuted)
                }
            },
        )
        null -> Unit
    }
}

@Composable
private fun ClientsHeader(
    dateFilter: ClientsDateFilter,
    onFilterChange: (ClientsDateFilter) -> Unit,
) {
    val tc = clientsStrings()
    var menuOpen by remember { mutableStateOf(false) }
    Row(
        Modifier.fillMaxWidth(),
        horizontalArrangement = Arrangement.SpaceBetween,
        verticalAlignment = Alignment.Top,
    ) {
        Column(Modifier.weight(1f)) {
            Text(tc.title, fontSize = 24.sp, fontWeight = FontWeight.Bold, color = AdminColors.Text)
            Text(
                tc.subtitle,
                color = AdminColors.TextMuted,
                fontSize = 13.sp,
                modifier = Modifier.padding(top = 4.dp),
            )
        }
        Box {
            OutlinedButton(
                onClick = { menuOpen = true },
                shape = RoundedCornerShape(10.dp),
            ) {
                Text(dateFilter.label(), color = AdminColors.Text)
            }
            DropdownMenu(expanded = menuOpen, onDismissRequest = { menuOpen = false }) {
                ClientsDateFilter.entries.forEach { opt ->
                    DropdownMenuItem(
                        text = { Text(opt.label()) },
                        onClick = {
                            onFilterChange(opt)
                            menuOpen = false
                        },
                    )
                }
            }
        }
    }
}

@Composable
private fun ClientsKpiRow(
    total: Int,
    active: Int,
    inactive: Int,
    withApp: Int,
    compact: Boolean,
) {
    val tc = clientsStrings()
    val items = listOf(
        Triple(tc.kpiTotalClients, total.toString(), AdminColors.Primary),
        Triple(tc.kpiActive, active.toString(), Color(0xFF2E7D32)),
        Triple(tc.kpiInactive, inactive.toString(), AdminColors.TextMuted),
        Triple(tc.kpiWithApp, withApp.toString(), AdminColors.Primary),
    )
    if (compact) {
        Column(verticalArrangement = Arrangement.spacedBy(10.dp)) {
            items.chunked(2).forEach { row ->
                Row(horizontalArrangement = Arrangement.spacedBy(10.dp), modifier = Modifier.fillMaxWidth()) {
                    row.forEach { (label, value, accent) ->
                        KpiCard(label, value, accent, Modifier.weight(1f))
                    }
                }
            }
        }
    } else {
        Row(horizontalArrangement = Arrangement.spacedBy(12.dp), modifier = Modifier.fillMaxWidth()) {
            items.forEach { (label, value, accent) ->
                KpiCard(label, value, accent, Modifier.weight(1f))
            }
        }
    }
}

@Composable
private fun KpiCard(label: String, value: String, accent: Color, modifier: Modifier = Modifier) {
    Column(
        modifier
            .clip(RoundedCornerShape(14.dp))
            .background(AdminColors.SurfaceContainer)
            .padding(16.dp),
    ) {
        Text(label, color = AdminColors.TextMuted, fontSize = 12.sp)
        Spacer(Modifier.height(6.dp))
        Text(value, fontWeight = FontWeight.Bold, fontSize = 22.sp, color = accent)
    }
}

@Composable
private fun ClientsDirectoryCard(
    clients: List<AdminClient>,
    totalCount: Int,
    listPaging: AdminListPaging,
    loading: Boolean,
    query: String,
    onQueryChange: (String) -> Unit,
    compact: Boolean,
    onCreate: () -> Unit,
    onView: (AdminClient) -> Unit,
    onEdit: (AdminClient) -> Unit,
    onDelete: (AdminClient) -> Unit,
    licensed: Set<String>?,
) {
    val tc = clientsStrings()
    Column(
        Modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(16.dp))
            .background(AdminColors.SurfaceContainer)
            .padding(16.dp),
    ) {
        if (compact) {
            Column(verticalArrangement = Arrangement.spacedBy(10.dp)) {
                Text(tc.directoryTitle, fontWeight = FontWeight.Bold, fontSize = 16.sp)
                ClientsSearchField(query, onQueryChange)
                if (canWriteModule(AdminModuleAccess.CLIENTS)) Button(
                    onClick = onCreate,
                    modifier = Modifier.fillMaxWidth().height(44.dp),
                    shape = RoundedCornerShape(10.dp),
                    colors = ButtonDefaults.buttonColors(
                        containerColor = AdminColors.Primary,
                        contentColor = AdminColors.OnBrand,
                    ),
                ) {
                    Text(tc.newClient, fontWeight = FontWeight.SemiBold)
                }
            }
        } else {
            Row(
                Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically,
            ) {
                Text(tc.directoryTitle, fontWeight = FontWeight.Bold, fontSize = 16.sp)
                Row(horizontalArrangement = Arrangement.spacedBy(10.dp), verticalAlignment = Alignment.CenterVertically) {
                    Box(Modifier.widthIn(max = 220.dp)) {
                        ClientsSearchField(query, onQueryChange)
                    }
                    if (canWriteModule(AdminModuleAccess.CLIENTS)) Button(
                        onClick = onCreate,
                        shape = RoundedCornerShape(10.dp),
                        colors = ButtonDefaults.buttonColors(
                            containerColor = AdminColors.Primary,
                            contentColor = AdminColors.OnBrand,
                        ),
                    ) {
                        Text(tc.newClient, fontWeight = FontWeight.SemiBold)
                    }
                }
            }
        }
        Spacer(Modifier.height(14.dp))
        when {
            loading -> Box(Modifier.fillMaxWidth().padding(32.dp), contentAlignment = Alignment.Center) {
                CircularProgressIndicator(color = AdminColors.Primary)
            }
            totalCount == 0 -> Text(tc.emptyList, color = AdminColors.TextMuted)
            compact -> {
                Column(
                    Modifier.fillMaxWidth(),
                    verticalArrangement = Arrangement.spacedBy(10.dp),
                ) {
                    clients.forEach { c ->
                        ClientMobileCard(c, onView, onEdit, onDelete, licensed)
                    }
                }
                AdminListPaginationBar(
                    totalItems = totalCount,
                    page = listPaging.page,
                    onPageChange = { listPaging.page = it },
                )
            }
            else -> {
                ClientsTable(
                    clients,
                    listPaging = listPaging,
                    onView,
                    onEdit,
                    onDelete,
                    licensed = licensed,
                    modifier = Modifier.fillMaxWidth(),
                )
                AdminListPaginationBar(
                    totalItems = totalCount,
                    page = listPaging.page,
                    onPageChange = { listPaging.page = it },
                )
            }
        }
    }
}

@Composable
private fun ClientsSearchField(query: String, onQueryChange: (String) -> Unit) {
    val tc = clientsStrings()
    OutlinedTextField(
        value = query,
        onValueChange = onQueryChange,
        modifier = Modifier.fillMaxWidth(),
        singleLine = true,
        placeholder = { Text(tc.searchPlaceholder, color = AdminColors.TextMuted) },
        shape = RoundedCornerShape(10.dp),
        colors = OutlinedTextFieldDefaults.colors(
            focusedBorderColor = AdminColors.Primary,
            unfocusedBorderColor = AdminColors.Outline,
        ),
    )
}

/** Códigos de licencia que habilitan un canal de socio (los mismos de /api/license/status). */
private val AppChannelLicenseCodes = setOf("portal", "mobile_android", "mobile_ios")

/** Canales que la licencia permite y el socio ya usó, en el orden de la columna. */
private fun appChannelLabels(client: AdminClient, licensed: Set<String>): List<String> = buildList {
    if ("portal" in licensed && "WEB" in client.accessChannels) add("Portal")
    if ("mobile_android" in licensed && "ANDROID" in client.accessChannels) add("App Android")
    if ("mobile_ios" in licensed && "IOS" in client.accessChannels) add("App iPhone")
}

/** Texto de la columna App: canales permitidos por la licencia, o "Sin usar" / "Sin acceso". */
private fun appChannelText(client: AdminClient, licensed: Set<String>): String {
    val labels = appChannelLabels(client, licensed)
    return when {
        labels.isNotEmpty() -> labels.joinToString(" · ")
        !client.portalEnabled -> "Sin acceso"
        else -> "Sin usar"
    }
}

@Composable
private fun AppChannelsCell(client: AdminClient, licensed: Set<String>) {
    val used = appChannelLabels(client, licensed).isNotEmpty()
    Text(
        appChannelText(client, licensed),
        color = if (used) AdminColors.Text else AdminColors.TextMuted,
        fontSize = 12.sp,
    )
}

/** Códigos de licencia de los canales de socio; null mientras carga. */
@Composable
private fun rememberLicensedAppChannels(): Set<String>? {
    var licensed by remember { mutableStateOf<Set<String>?>(null) }
    LaunchedEffect(Unit) {
        licensed = runCatching { AdminRepo.api.getProductLicenseStatus().features }
            .getOrDefault(emptySet())
            .intersect(AppChannelLicenseCodes)
    }
    return licensed
}

@Composable
private fun ClientsTable(
    clients: List<AdminClient>,
    listPaging: AdminListPaging,
    onView: (AdminClient) -> Unit,
    onEdit: (AdminClient) -> Unit,
    onDelete: (AdminClient) -> Unit,
    licensed: Set<String>?,
    modifier: Modifier = Modifier,
) {
    val t = strings()
    val tc = clientsStrings()
    // La columna App solo aparece si la licencia incluye portal o alguna app móvil.
    val showApp = !licensed.isNullOrEmpty()
    Column(modifier.fillMaxWidth()) {
        Row(
            Modifier
                .fillMaxWidth()
                .padding(vertical = 8.dp, horizontal = 4.dp),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(4.dp),
        ) {
            AdminSortableHeader(tc.colClient, "name", listPaging.sort, listPaging::onSort, Modifier.weight(1.6f))
            AdminSortableHeader(t.common.status, "status", listPaging.sort, listPaging::onSort, Modifier.weight(0.8f))
            if (showApp) {
                AdminSortableHeader(tc.colApp, "portal", listPaging.sort, listPaging::onSort, Modifier.weight(0.7f))
            }
            AdminListHeaderLabel("Membresía", Modifier.weight(1.2f))
            AdminListHeaderLabel(t.common.actions, Modifier.width(140.dp))
        }
        HorizontalDivider(color = AdminColors.Outline.copy(alpha = 0.5f))
        clients.forEach { c ->
                Row(
                    Modifier
                        .fillMaxWidth()
                        .padding(vertical = 10.dp, horizontal = 4.dp),
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.spacedBy(4.dp),
                ) {
                    Row(Modifier.weight(1.6f), verticalAlignment = Alignment.CenterVertically) {
                        Box(
                            Modifier
                                .size(8.dp)
                                .clip(CircleShape)
                                .background(
                                    if (c.status == AdminClientStatus.ACTIVE) AdminColors.Primary
                                    else AdminColors.TextMuted,
                                ),
                        )
                        Spacer(Modifier.width(10.dp))
                        Column {
                            Text(c.fullName, fontWeight = FontWeight.SemiBold, color = AdminColors.Text)
                            Text(c.email ?: t.common.emptyValue, color = AdminColors.TextMuted, fontSize = 12.sp)
                        }
                    }
                    Box(Modifier.weight(0.8f)) { StatusBadge(c.status) }
                    if (showApp) {
                        Box(Modifier.weight(0.7f)) { AppChannelsCell(c, licensed.orEmpty()) }
                    }
                    Text(
                        c.membershipPlanName?.takeIf { it.isNotBlank() } ?: t.common.emptyValue,
                        Modifier.weight(1.2f),
                        color = AdminColors.Text,
                        fontSize = 13.sp,
                        maxLines = 1,
                    )
                    Row(Modifier.width(140.dp)) {
                        IconButton(onClick = { onView(c) }) {
                            Icon(Icons.Outlined.Search, contentDescription = tc.viewAction, tint = AdminColors.TextMuted)
                        }
                        if (canWriteModule(AdminModuleAccess.CLIENTS)) IconButton(onClick = { onEdit(c) }) {
                            Icon(Icons.Outlined.Edit, contentDescription = t.common.edit, tint = AdminColors.Primary)
                        }
                        if (canDeleteModule(AdminModuleAccess.CLIENTS)) IconButton(onClick = { onDelete(c) }) {
                            Icon(Icons.Outlined.Delete, contentDescription = t.common.delete, tint = AdminColors.Error)
                        }
                    }
                }
                HorizontalDivider(color = AdminColors.Outline.copy(alpha = 0.35f))
        }
    }
}

@Composable
private fun ClientMobileCard(
    client: AdminClient,
    onView: (AdminClient) -> Unit,
    onEdit: (AdminClient) -> Unit,
    onDelete: (AdminClient) -> Unit,
    licensed: Set<String>?,
) {
    val t = strings()
    Column(
        Modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(12.dp))
            .border(1.dp, AdminColors.Outline.copy(alpha = 0.6f), RoundedCornerShape(12.dp))
            .padding(14.dp),
    ) {
        Text(client.fullName, fontWeight = FontWeight.SemiBold, color = AdminColors.Text)
        Text(client.email ?: t.common.emptyValue, color = AdminColors.TextMuted, fontSize = 12.sp)
        Spacer(Modifier.height(8.dp))
        Row(horizontalArrangement = Arrangement.spacedBy(8.dp), verticalAlignment = Alignment.CenterVertically) {
            StatusBadge(client.status)
            if (!licensed.isNullOrEmpty()) AppChannelsCell(client, licensed)
        }
        Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.End) {
            IconButton(onClick = { onView(client) }) {
                Icon(Icons.Outlined.Search, null, tint = AdminColors.TextMuted)
            }
            if (canWriteModule(AdminModuleAccess.CLIENTS)) IconButton(onClick = { onEdit(client) }) {
                Icon(Icons.Outlined.Edit, null, tint = AdminColors.Primary)
            }
            if (canDeleteModule(AdminModuleAccess.CLIENTS)) IconButton(onClick = { onDelete(client) }) {
                Icon(Icons.Outlined.Delete, null, tint = AdminColors.Error)
            }
        }
    }
}

@Composable
private fun StatusBadge(status: AdminClientStatus) {
    val bg = if (status == AdminClientStatus.ACTIVE) Color(0xFFE8F5E9) else Color(0xFFF3F4F6)
    val fg = if (status == AdminClientStatus.ACTIVE) Color(0xFF2E7D32) else AdminColors.TextMuted
    Text(
        status.label(),
        color = fg,
        fontSize = 12.sp,
        fontWeight = FontWeight.SemiBold,
        modifier = Modifier
            .clip(RoundedCornerShape(8.dp))
            .background(bg)
            .padding(horizontal = 10.dp, vertical = 4.dp),
    )
}

/**
 * Acceso del socio al portal y a la app. Solo lo ve quien administra clientes; el socio nunca lo ve en su perfil.
 * Activar genera una contraseña temporal y la envía por correo; si no se puede, se muestra una vez para entregarla.
 */
@Composable
private fun PortalAccessSection(client: AdminClient) {
    if (!canWriteModule(AdminModuleAccess.CLIENTS)) return
    val scope = rememberCoroutineScope()
    var busy by remember(client.id) { mutableStateOf(false) }
    var result by remember(client.id) { mutableStateOf<AdminPortalActivation?>(null) }
    var error by remember(client.id) { mutableStateOf<String?>(null) }
    Column(Modifier.fillMaxWidth().padding(top = 12.dp)) {
        Text("Acceso al portal y app", fontWeight = FontWeight.SemiBold, fontSize = 13.sp, color = AdminColors.Text)
        Text(
            if (client.portalEnabled) "Activo" else "Inactivo",
            fontSize = 12.sp,
            color = if (client.portalEnabled) AdminColors.Primary else AdminColors.TextMuted,
        )
        Spacer(Modifier.height(6.dp))
        Button(
            onClick = {
                scope.launch {
                    busy = true
                    error = null
                    try {
                        result = AdminRepo.api.activateClientPortal(client.id)
                    } catch (e: Exception) {
                        error = e.userMessage("No se pudo activar el acceso.")
                    } finally {
                        busy = false
                    }
                }
            },
            enabled = !busy,
            shape = RoundedCornerShape(10.dp),
        ) {
            Text(if (client.portalEnabled) "Reenviar acceso" else "Activar acceso")
        }
        result?.let { r ->
            Spacer(Modifier.height(6.dp))
            Text(r.message, fontSize = 12.sp, color = AdminColors.Text)
            r.temporaryPassword?.let { pwd ->
                Text(
                    "Contraseña temporal: $pwd",
                    fontWeight = FontWeight.SemiBold,
                    fontSize = 13.sp,
                    color = AdminColors.Text,
                )
            }
        }
        error?.let { Text(it, color = AdminColors.Error, fontSize = 12.sp) }
    }
}

@Composable
private fun ClientDetailDialog(
    client: AdminClient,
    licensed: Set<String>?,
    onDismiss: () -> Unit,
) {
    val t = strings()
    val tc = clientsStrings()
    var photoBytes by remember(client.id) { mutableStateOf<ByteArray?>(null) }
    var showAccount by remember(client.id) { mutableStateOf(false) }
    var walletModuleEnabled by remember { mutableStateOf(false) }
    LaunchedEffect(Unit) {
        walletModuleEnabled = runCatching {
            AdminRepo.api.getSettingsModules().clientAccountsEnabled
        }.getOrDefault(false)
    }
    LaunchedEffect(client.id, client.photoUrl) {
        photoBytes = if (!client.photoUrl.isNullOrBlank()) {
            runCatching { AdminRepo.api.fetchClientPhotoBytes(client.id) }.getOrNull()
        } else {
            null
        }
    }
    val previewModel = imagePreviewModel(photoBytes)
    val initials = "${client.firstName.firstOrNull() ?: '?'}${client.lastName.firstOrNull() ?: ""}".uppercase()
    val terminalStatus = when {
        client.accessSyncedAt != null && client.accessSyncError == null -> tc.syncOk
        client.accessSyncedAt != null -> tc.syncWithWarning
        else -> tc.syncPending
    }

    Dialog(
        onDismissRequest = onDismiss,
        properties = androidx.compose.ui.window.DialogProperties(usePlatformDefaultWidth = false),
    ) {
        VerticalScrollColumn(
            Modifier
                .fillMaxWidth(0.94f)
                .widthIn(max = 560.dp)
                .clip(RoundedCornerShape(16.dp))
                .background(AdminColors.SurfaceContainer)
                .padding(20.dp),
        ) {
            Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween) {
                Text(tc.detailTitle, fontWeight = FontWeight.Bold, fontSize = 18.sp)
                Icon(
                    Icons.Outlined.Close,
                    contentDescription = t.common.close,
                    tint = AdminColors.TextMuted,
                    modifier = Modifier.minimumInteractiveComponentSize().clickable(onClick = onDismiss).size(22.dp),
                )
            }
            Spacer(Modifier.height(16.dp))

            Row(
                Modifier.fillMaxWidth(),
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(16.dp),
            ) {
                Box(
                    Modifier
                        .size(88.dp)
                        .clip(CircleShape)
                        .background(AdminColors.Outline.copy(alpha = 0.25f))
                        .border(1.dp, AdminColors.Outline.copy(alpha = 0.45f), CircleShape),
                    contentAlignment = Alignment.Center,
                ) {
                    if (previewModel != null) {
                        key(previewModel.takeLast(32)) {
                            AsyncImage(
                                model = previewModel,
                                contentDescription = tc.clientPhoto,
                                modifier = Modifier.fillMaxSize(),
                                contentScale = ContentScale.Crop,
                            )
                        }
                    } else {
                        Text(initials, fontWeight = FontWeight.Bold, fontSize = 28.sp, color = AdminColors.Primary)
                    }
                }
                Column(Modifier.weight(1f)) {
                    Text(client.fullName, fontWeight = FontWeight.Bold, fontSize = 17.sp, color = AdminColors.Text)
                    Text(client.email ?: t.common.emptyValue, color = AdminColors.TextMuted, fontSize = 13.sp)
                    Spacer(Modifier.height(6.dp))
                    Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                        StatusBadge(client.status)
                        if (!licensed.isNullOrEmpty()) AppChannelsCell(client, licensed)
                    }
                }
            }

            Spacer(Modifier.height(16.dp))
            val leftDetails: @Composable () -> Unit = {
                DetailRow(t.common.name, client.firstName)
                DetailRow(t.common.email, client.email ?: t.common.emptyValue)
                DetailRow(t.common.sex, client.sex?.label() ?: t.common.emptyValue)
                DetailRow(t.common.status, client.status.label())
                DetailRow(tc.branch, client.branchName ?: t.common.emptyValue)
            }
            val rightDetails: @Composable () -> Unit = {
                DetailRow(t.common.lastName, client.lastName)
                DetailRow(t.common.phone, client.phone ?: t.common.emptyValue)
                DetailRow(tc.documentLabel, client.docId ?: t.common.emptyValue)
                DetailRow(t.common.address, client.address ?: t.common.emptyValue)
                if (!licensed.isNullOrEmpty()) DetailRow(tc.colApp, appChannelText(client, licensed))
                DetailRow("Membresía", client.membershipPlanName?.takeIf { it.isNotBlank() } ?: t.common.emptyValue)
                DetailRow(tc.terminalHikvision, terminalStatus)
            }
            // Celular: una columna (el correo y los datos no se cortan). Escritorio: dos columnas.
            BoxWithConstraints(Modifier.fillMaxWidth()) {
                if (maxWidth < 480.dp) {
                    Column(Modifier.fillMaxWidth()) {
                        leftDetails()
                        rightDetails()
                    }
                } else {
                    Row(
                        Modifier.fillMaxWidth(),
                        horizontalArrangement = Arrangement.spacedBy(20.dp),
                    ) {
                        Column(Modifier.weight(1f)) { leftDetails() }
                        Column(Modifier.weight(1f)) { rightDetails() }
                    }
                }
            }
            PortalAccessSection(client = client)

            client.accessSyncError?.let { raw ->
                Spacer(Modifier.height(8.dp))
                Column(
                    Modifier
                        .fillMaxWidth()
                        .clip(RoundedCornerShape(10.dp))
                        .background(Color(0xFFFFF3E0))
                        .padding(12.dp),
                ) {
                    Text(
                        tc.syncWarningTitle,
                        color = Color(0xFFE65100),
                        fontSize = 12.sp,
                        fontWeight = FontWeight.SemiBold,
                    )
                    Spacer(Modifier.height(4.dp))
                    Text(
                        formatAccessSyncError(raw, tc.terminalSyncFailed),
                        color = Color(0xFF5D4037),
                        fontSize = 12.sp,
                    )
                }
            }

            if (walletModuleEnabled) {
                Spacer(Modifier.height(16.dp))
                Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                    OutlinedButton(onClick = { showAccount = true }) {
                        Text(tc.wallet)
                    }
                }
            }

            Spacer(Modifier.height(8.dp))
            Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.End) {
                Button(
                    onClick = onDismiss,
                    colors = ButtonDefaults.buttonColors(
                        containerColor = AdminColors.Primary,
                        contentColor = AdminColors.OnBrand,
                    ),
                ) {
                    Text(t.common.close)
                }
            }
        }
    }

    if (showAccount && walletModuleEnabled) {
        ClientAccountDetailDialog(
            clientId = client.id,
            onDismiss = { showAccount = false },
        )
    }
}

private fun formatAccessSyncError(raw: String, isapiFallback: String): String {
    val errorMsg = Regex(""""errorMsg"\s*:\s*"([^"]*)"""").find(raw)?.groupValues?.getOrNull(1)
    val status = Regex(""""statusString"\s*:\s*"([^"]*)"""").find(raw)?.groupValues?.getOrNull(1)
    return when {
        !errorMsg.isNullOrBlank() && !status.isNullOrBlank() -> "$status: $errorMsg"
        !errorMsg.isNullOrBlank() -> errorMsg
        raw.contains("ISAPI", ignoreCase = true) -> isapiFallback
        else -> raw.take(200).let { if (raw.length > 200) "$it…" else it }
    }
}

@Composable
private fun DetailRow(label: String, value: String) {
    Column(Modifier.padding(bottom = 10.dp)) {
        Text(label, color = AdminColors.TextMuted, fontSize = 12.sp)
        Text(value, color = AdminColors.Text, fontSize = 14.sp, fontWeight = FontWeight.Medium)
    }
}

@Composable
private fun ClientFormDialog(
    title: String,
    initial: AdminClient?,
    companyId: String?,
    saving: Boolean,
    onDismiss: () -> Unit,
    onSave: (ClientFormSubmit) -> Unit,
) {
    val t = strings()
    val tc = clientsStrings()
    var firstName by remember(initial) { mutableStateOf(initial?.firstName.orEmpty()) }
    var lastName by remember(initial) { mutableStateOf(initial?.lastName.orEmpty().let { if (it == "—") "" else it }) }
    var email by remember(initial) { mutableStateOf(initial?.email.orEmpty()) }
    var phone by remember(initial) { mutableStateOf(initial?.phone.orEmpty()) }
    var docId by remember(initial) { mutableStateOf(initial?.docId.orEmpty()) }
    var sex by remember(initial) { mutableStateOf(initial?.sex) }
    var address by remember(initial) { mutableStateOf(initial?.address.orEmpty()) }
    var status by remember(initial) { mutableStateOf(initial?.status ?: AdminClientStatus.ACTIVE) }
    var inactivationReason by remember(initial) { mutableStateOf(initial?.inactivationReason) }
    var inactivationNote by remember(initial) { mutableStateOf(initial?.inactivationNote.orEmpty()) }
    var reasonMenu by remember { mutableStateOf(false) }
    var appEnabled by remember(initial) { mutableStateOf(initial?.portalEnabled == true) }
    var portalPassword by remember { mutableStateOf("") }
    val branchId = companyId
    var formError by remember { mutableStateOf<String?>(null) }
    var statusMenu by remember { mutableStateOf(false) }
    var sexMenu by remember { mutableStateOf(false) }
    var pendingPhoto by remember { mutableStateOf<PickedImagePayload?>(null) }
    var pendingPhotoName by remember { mutableStateOf<String?>(null) }
    var removePhoto by remember { mutableStateOf(false) }
    var existingPhotoBytes by remember { mutableStateOf<ByteArray?>(null) }
    var photoRevision by remember { mutableStateOf(0) }
    var showTerminalCapture by remember { mutableStateOf(false) }
    val focusFirstName = remember { FocusRequester() }
    val focusLastName = remember { FocusRequester() }
    val focusEmail = remember { FocusRequester() }
    val focusPhone = remember { FocusRequester() }
    val focusDocId = remember { FocusRequester() }
    val focusAddress = remember { FocusRequester() }
    val focusPassword = remember { FocusRequester() }

    LaunchedEffect(Unit) {
        runCatching { focusFirstName.requestFocus() }
    }

    LaunchedEffect(initial?.id, initial?.photoUrl) {
        pendingPhoto = null
        pendingPhotoName = null
        removePhoto = false
        existingPhotoBytes = null
        val id = initial?.id
        if (id != null && !initial.photoUrl.isNullOrBlank()) {
            existingPhotoBytes = runCatching { AdminRepo.api.fetchClientPhotoBytes(id) }.getOrNull()
        }
        photoRevision += 1
    }

    fun pickPhoto() {
        openImagePicker { picked ->
            if (picked == null) return@openImagePicker
            pendingPhoto = PickedImagePayload(picked.bytes, picked.fileName, picked.mimeType)
            pendingPhotoName = picked.fileName
            removePhoto = false
            photoRevision += 1
        }
    }

    fun applyCapturedPhoto(payload: PickedImagePayload) {
        pendingPhoto = payload
        pendingPhotoName = payload.fileName
        removePhoto = false
        photoRevision += 1
    }

    if (showTerminalCapture) {
        TerminalPhotoCaptureDialog(
            onDismiss = { showTerminalCapture = false },
            onCaptured = { applyCapturedPhoto(it) },
        )
    }

    Dialog(onDismissRequest = onDismiss) {
        Column(
            Modifier
                .widthIn(max = 560.dp)
                .fillMaxWidth()
                .verticalScroll(rememberScrollState())
                .clip(RoundedCornerShape(16.dp))
                .background(AdminColors.SurfaceContainer)
                .padding(20.dp),
        ) {
            Text(title, fontWeight = FontWeight.Bold, fontSize = 18.sp)
            Spacer(Modifier.height(14.dp))
            ClientPhotoField(
                firstName = firstName.ifBlank { "?" },
                lastName = lastName.ifBlank { "" },
                pendingPhoto = pendingPhoto,
                existingPhotoBytes = existingPhotoBytes,
                pendingFileName = pendingPhotoName,
                removePhoto = removePhoto,
                enabled = !saving,
                onPickPhoto = { pickPhoto() },
                onCaptureFromTerminal = { showTerminalCapture = true },
                onClearPhoto = {
                    pendingPhoto = null
                    pendingPhotoName = null
                    removePhoto = true
                    photoRevision += 1
                },
            )
            Spacer(Modifier.height(14.dp))
            Row(
                Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.spacedBy(16.dp),
            ) {
                Column(Modifier.weight(1f)) {
                    FormLabel("Nombre *")
                    FormField(
                        firstName,
                        { firstName = it },
                        "Nombre",
                        focusRequester = focusFirstName,
                        onNext = { focusLastName.requestFocus() },
                        onPrev = { },
                    )
                    Spacer(Modifier.height(10.dp))
                    FormLabel("Correo (opcional)")
                    FormField(
                        email,
                        { email = it },
                        "correo@ejemplo.com",
                        focusRequester = focusEmail,
                        onNext = { focusPhone.requestFocus() },
                        onPrev = { focusLastName.requestFocus() },
                    )
                    Spacer(Modifier.height(10.dp))
                    FormLabel("Sexo *")
                    Box {
                        OutlinedButton(onClick = { sexMenu = true }, modifier = Modifier.fillMaxWidth()) {
                            Text(sex?.labelEs() ?: "Seleccionar", color = AdminColors.Text)
                        }
                        DropdownMenu(expanded = sexMenu, onDismissRequest = { sexMenu = false }) {
                            AdminPersonSex.entries.forEach { option ->
                                DropdownMenuItem(
                                    text = { Text(option.labelEs()) },
                                    onClick = {
                                        sex = option
                                        sexMenu = false
                                    },
                                )
                            }
                        }
                    }
                    Spacer(Modifier.height(10.dp))
                    FormLabel("Estado")
                    Box {
                        OutlinedButton(onClick = { statusMenu = true }, modifier = Modifier.fillMaxWidth()) {
                            Text(status.labelEs(), color = AdminColors.Text)
                        }
                        DropdownMenu(expanded = statusMenu, onDismissRequest = { statusMenu = false }) {
                            AdminClientStatus.entries.forEach { s ->
                                DropdownMenuItem(
                                    text = { Text(s.labelEs()) },
                                    onClick = {
                                        status = s
                                        statusMenu = false
                                    },
                                )
                            }
                        }
                    }
                }
                Column(Modifier.weight(1f)) {
                    FormLabel("Apellido *")
                    FormField(
                        lastName,
                        { lastName = it },
                        "Apellido",
                        focusRequester = focusLastName,
                        onNext = { focusEmail.requestFocus() },
                        onPrev = { focusFirstName.requestFocus() },
                    )
                    Spacer(Modifier.height(10.dp))
                    FormLabel("Teléfono (opcional)")
                    FormField(
                        phone,
                        { phone = it },
                        "Ej. +58 412 0000000",
                        focusRequester = focusPhone,
                        onNext = { focusDocId.requestFocus() },
                        onPrev = { focusEmail.requestFocus() },
                    )
                    Spacer(Modifier.height(10.dp))
                    FormLabel("Documento")
                    FormField(
                        docId,
                        { docId = it },
                        "Cédula o pasaporte",
                        focusRequester = focusDocId,
                        onNext = { focusAddress.requestFocus() },
                        onPrev = { focusPhone.requestFocus() },
                    )
                    // Motivo en la columna derecha, al mismo nivel que el Estado de la columna izquierda.
                    if (status == AdminClientStatus.INACTIVE) {
                        Spacer(Modifier.height(10.dp))
                        FormLabel("Motivo de inactivación *")
                        Box {
                            OutlinedButton(onClick = { reasonMenu = true }, modifier = Modifier.fillMaxWidth()) {
                                Text(
                                    (CLIENT_INACTIVATION_REASONS + CLIENT_AUTO_INACTIVATION_REASON)
                                        .firstOrNull { it.first == inactivationReason }?.second
                                        ?: "Seleccionar",
                                    color = AdminColors.Text,
                                )
                            }
                            DropdownMenu(expanded = reasonMenu, onDismissRequest = { reasonMenu = false }) {
                                CLIENT_INACTIVATION_REASONS.forEach { (code, label) ->
                                    DropdownMenuItem(
                                        text = { Text(label) },
                                        onClick = {
                                            inactivationReason = code
                                            reasonMenu = false
                                        },
                                    )
                                }
                            }
                        }
                        if (inactivationReason == "OTRO") {
                            Spacer(Modifier.height(10.dp))
                            FormLabel("Describe el motivo *")
                            FormField(
                                inactivationNote,
                                { inactivationNote = it },
                                "Motivo",
                            )
                        }
                    }
                }
            }
            Spacer(Modifier.height(10.dp))
            FormLabel("Dirección *")
            FormField(
                address,
                { address = it },
                "Calle, número, ciudad",
                focusRequester = focusAddress,
                onNext = {
                    if (appEnabled) focusPassword.requestFocus()
                },
                onPrev = { focusDocId.requestFocus() },
                imeAction = if (appEnabled) ImeAction.Next else ImeAction.Done,
            )
            Spacer(Modifier.height(10.dp))
            Row(verticalAlignment = Alignment.CenterVertically) {
                Checkbox(checked = appEnabled, onCheckedChange = { appEnabled = it })
                Text("App (portal de miembro habilitado)", color = AdminColors.Text, fontSize = 13.sp)
            }
            if (appEnabled) {
                FormLabel(
                    if (initial?.portalEnabled == true) {
                        "Nueva contraseña app (opcional)"
                    } else {
                        "Contraseña app socio (opcional)"
                    },
                )
                FormField(
                    portalPassword,
                    { portalPassword = it },
                    "Mínimo 8 caracteres",
                    isPassword = true,
                    focusRequester = focusPassword,
                    onNext = { },
                    onPrev = { focusAddress.requestFocus() },
                    imeAction = ImeAction.Done,
                )
                PasswordRulesHint(portalPassword, muted = AdminColors.TextMuted)
                GeneratePasswordButton(
                    value = portalPassword,
                    onGenerated = { portalPassword = it },
                    onClear = { portalPassword = "" },
                )
            }
            formError?.let {
                Text(it, color = AdminColors.Error, fontSize = 12.sp, modifier = Modifier.padding(top = 10.dp))
            }
            Spacer(Modifier.height(18.dp))
            Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.End) {
                TextButton(onClick = onDismiss, enabled = !saving) {
                    Text("Cancelar", color = AdminColors.TextMuted)
                }
                Spacer(Modifier.width(8.dp))
                Button(
                    onClick = {
                        val first = firstName.trim()
                        val last = lastName.trim()
                        if (first.isEmpty()) {
                            formError = "El nombre es obligatorio."
                            return@Button
                        }
                        if (last.isEmpty()) {
                            formError = "El apellido es obligatorio."
                            return@Button
                        }
                        if (!email.isValidEmailOrBlank()) {
                            formError = "El correo debe incluir @ y un dominio (ej. nombre@correo.com)."
                            return@Button
                        }
                        val selectedSex = sex
                        if (selectedSex == null) {
                            formError = "El sexo es obligatorio."
                            return@Button
                        }
                        val addr = address.trim()
                        if (addr.isEmpty()) {
                            formError = "La dirección es obligatoria."
                            return@Button
                        }
                        val wasEnabled = initial?.portalEnabled == true
                        if (appEnabled && portalPassword.isNotBlank() && PasswordRules.unmet(portalPassword.trim()).isNotEmpty()) {
                            formError = "La contraseña del portal debe tener al menos 8 caracteres, una letra y un número."
                            return@Button
                        }
                        val inactive = status == AdminClientStatus.INACTIVE
                        if (inactive && inactivationReason == null) {
                            formError = "Indica el motivo de la inactivación."
                            return@Button
                        }
                        if (inactive && inactivationReason == "OTRO" && inactivationNote.isBlank()) {
                            formError = "Describe el motivo de la inactivación."
                            return@Button
                        }
                        formError = null
                        onSave(
                            ClientFormSubmit(
                                upsert = AdminClientUpsert(
                                    firstName = first,
                                    lastName = last,
                                    email = email.trim().ifBlank { null },
                                    phone = phone.trim().ifBlank { null },
                                    docId = docId.trim().ifBlank { null },
                                    status = status,
                                    portalPassword = portalPassword.trim().takeIf { it.isNotEmpty() },
                                    branchId = branchId,
                                    disablePortal = wasEnabled && !appEnabled,
                                    sex = selectedSex,
                                    address = addr,
                                    inactivationReason = if (inactive) inactivationReason else null,
                                    inactivationNote = if (inactive) inactivationNote.trim().ifBlank { null } else null,
                                ),
                                photo = pendingPhoto,
                                removePhoto = removePhoto,
                            ),
                        )
                    },
                    enabled = !saving,
                    colors = ButtonDefaults.buttonColors(
                        containerColor = AdminColors.Primary,
                        contentColor = AdminColors.OnBrand,
                    ),
                ) {
                    if (saving) {
                        CircularProgressIndicator(
                            modifier = Modifier.size(18.dp),
                            color = AdminColors.OnBrand,
                            strokeWidth = 2.dp,
                        )
                    } else {
                        Text("Guardar")
                    }
                }
            }
        }
    }
}

@Composable
private fun FormLabel(text: String) {
    Text(
        text,
        fontSize = 13.sp,
        fontWeight = FontWeight.SemiBold,
        color = AdminColors.Text,
        modifier = Modifier.padding(bottom = 6.dp),
    )
}

@Composable
private fun FormField(
    value: String,
    onValueChange: (String) -> Unit,
    placeholder: String,
    isPassword: Boolean = false,
    focusRequester: FocusRequester? = null,
    onNext: (() -> Unit)? = null,
    onPrev: (() -> Unit)? = null,
    imeAction: ImeAction = ImeAction.Next,
) {
    val (visible, toggleVisible) = rememberPasswordVisible()
    fun goNext() {
        onNext?.invoke()
    }
    fun goPrev() {
        onPrev?.invoke()
    }
    OutlinedTextField(
        value = value,
        onValueChange = onValueChange,
        modifier = Modifier
            .fillMaxWidth()
            .then(if (focusRequester != null) Modifier.focusRequester(focusRequester) else Modifier)
            .onPreviewKeyEvent { event ->
                if (event.type != KeyEventType.KeyDown) return@onPreviewKeyEvent false
                when (event.key) {
                    Key.Enter, Key.NumPadEnter -> {
                        goNext()
                        true
                    }
                    Key.Tab -> {
                        if (event.isShiftPressed) goPrev() else goNext()
                        true
                    }
                    Key.DirectionDown -> {
                        goNext()
                        true
                    }
                    Key.DirectionUp -> {
                        goPrev()
                        true
                    }
                    else -> false
                }
            },
        singleLine = true,
        placeholder = { Text(placeholder, color = AdminColors.TextMuted) },
        visualTransformation = if (isPassword && !visible) PasswordVisualTransformation() else VisualTransformation.None,
        trailingIcon = if (isPassword) {
            { PasswordVisibilityToggle(visible = visible, onToggle = toggleVisible) }
        } else {
            null
        },
        keyboardOptions = KeyboardOptions(imeAction = imeAction),
        keyboardActions = KeyboardActions(
            onNext = { goNext() },
            onDone = { goNext() },
        ),
        shape = RoundedCornerShape(10.dp),
        colors = OutlinedTextFieldDefaults.colors(
            focusedBorderColor = AdminColors.Primary,
            unfocusedBorderColor = AdminColors.Outline,
        ),
    )
}
